import { apiClient } from '../lib/apiClient';
import type {
  Task,
  CreateTaskInput,
  UpdateTaskInput,
  CompleteTaskInput,
  SkipTaskInput,
  RecurrenceRule,
  RecurrenceScope,
} from '../types/task';

export type { CreateTaskInput, UpdateTaskInput, CompleteTaskInput, SkipTaskInput, RecurrenceScope };

/** Form/UI rule → API body (snake_case end date, only the fields the frequency uses). */
export function toApiRecurrenceRule(rule: RecurrenceRule | null | undefined): Record<string, unknown> | null {
  if (!rule) return null;
  const endDate = rule.endDate ?? rule.end_date ?? null;
  const usesInterval = rule.frequency === 'every_x_days' || rule.frequency === 'custom';
  return {
    frequency: rule.frequency,
    ...(usesInterval && rule.interval != null && !Number.isNaN(rule.interval) ? { interval: rule.interval } : {}),
    ...(rule.frequency === 'specific_weekdays' && rule.weekdays?.length ? { weekdays: rule.weekdays } : {}),
    ...(endDate ? { end_date: endDate } : {}),
  };
}

export interface TaskOccurrencesResponse {
  data: Task[];
}

export interface GroupedTasksResponse {
  overdue: Task[];
  today: Task[];
  upcoming: Task[];
  completed: Task[];
}

export interface TaskListResponse {
  data: GroupedTasksResponse;
}

export interface TaskDetailResponse {
  data: Task;
}

export interface CreateTaskResponse {
  data: Task;
}

export interface UpdateTaskResponse {
  data: Task;
}

export interface CompleteTaskResponse {
  data: Task;
}

export interface SkipTaskResponse {
  data: Task;
}

export interface DeleteTaskResponse {
  success: boolean;
}

export async function listTasks(aquariumId: string): Promise<TaskListResponse> {
  return apiClient.get<TaskListResponse>(
    `/api/v1/aquariums/${aquariumId}/tasks`,
  );
}

export async function getTaskById(taskId: string): Promise<TaskDetailResponse> {
  return apiClient.get<TaskDetailResponse>(
    `/api/v1/tasks/${taskId}`,
  );
}

export async function createTask(
  aquariumId: string,
  input: CreateTaskInput,
): Promise<CreateTaskResponse> {
  const body: Record<string, unknown> = {
    title: input.title,
    description: input.description ?? null,
    priority: input.priority,
    category: input.category,
    due_date: input.dueDate ?? null,
    due_time: input.dueTime ?? null,
    recurrence_rule: toApiRecurrenceRule(input.recurrenceRule),
    ...(input.emailReminder !== undefined ? { email_reminder: input.emailReminder } : {}),
  };
  return apiClient.post<CreateTaskResponse>(
    `/api/v1/aquariums/${aquariumId}/tasks`,
    body,
  );
}

/**
 * All occurrences (one-off tasks and every date of recurring tasks) whose due date
 * lies in [from, to] (YYYY-MM-DD) — the calendar feed.
 */
export async function listTaskOccurrences(
  aquariumId: string,
  from: string,
  to: string,
): Promise<TaskOccurrencesResponse> {
  const query = new URLSearchParams({ from, to }).toString();
  return apiClient.get<TaskOccurrencesResponse>(
    `/api/v1/aquariums/${aquariumId}/tasks/occurrences?${query}`,
  );
}

/**
 * Updates a task or one occurrence. `scope` (recurring tasks) chooses whether only
 * this occurrence, this and the following ones, or the whole series change.
 */
export async function updateTask(
  taskId: string,
  input: UpdateTaskInput,
  scope?: RecurrenceScope,
): Promise<UpdateTaskResponse> {
  const body: Record<string, unknown> = {};
  if (input.title !== undefined) body['title'] = input.title;
  if (input.description !== undefined) body['description'] = input.description;
  if (input.priority !== undefined) body['priority'] = input.priority;
  if (input.status !== undefined) body['status'] = input.status;
  if (input.category !== undefined) body['category'] = input.category;
  if (input.dueDate !== undefined) body['due_date'] = input.dueDate || null;
  if (input.dueTime !== undefined) body['due_time'] = input.dueTime || null;
  if (input.recurrenceRule !== undefined) body['recurrence_rule'] = toApiRecurrenceRule(input.recurrenceRule);
  if (input.emailReminder !== undefined) body['email_reminder'] = input.emailReminder;
  if (scope) body['scope'] = scope;
  return apiClient.patch<UpdateTaskResponse>(
    `/api/v1/tasks/${taskId}`,
    body,
  );
}

export async function completeTask(
  taskId: string,
  input?: CompleteTaskInput,
): Promise<CompleteTaskResponse> {
  return apiClient.post<CompleteTaskResponse>(
    `/api/v1/tasks/${taskId}/complete`,
    input?.completionNotes ? { completion_notes: input.completionNotes } : {},
  );
}

export async function skipTask(
  taskId: string,
  input?: SkipTaskInput,
): Promise<SkipTaskResponse> {
  return apiClient.post<SkipTaskResponse>(
    `/api/v1/tasks/${taskId}/skip`,
    input?.reason ? { skip_reason: input.reason } : {},
  );
}

export async function deleteTask(taskId: string, scope?: RecurrenceScope): Promise<DeleteTaskResponse> {
  await apiClient.delete(`/api/v1/tasks/${taskId}${scope ? `?scope=${scope}` : ''}`);
  return { success: true };
}
